import { LogOut, Clock, Crown, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ui/theme-toggle";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import appLogo from "@/images/notanaicon.svg";
import { navigationItems } from "./Sidebar";
import { cn } from '@/lib/utils';
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

type HeaderProps = {
  isMobile: boolean;
}

export function Header({ isMobile }: HeaderProps) {
  const { logout, subscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login"); // Keep this as absolute since /login is a public route
  };

  return (
    <header className="fixed top-0 z-50 w-full glass-header">
      <div className="flex h-16 items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <img src={appLogo} alt="Notana Logo" className="h-8 w-8 dark:invert" />
          <span className="text-xl font-bold hidden md:inline bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Notana</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          {/* Subscription Status Indicator */}
          {subscription && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  className="cursor-pointer"
                  onClick={() => navigate("/subscription-choice?change=true")}
                >
                  {subscription.status === 'trial' && (
                    <Badge variant="outline" className="flex items-center gap-1 py-1">
                      <Clock className="h-3 w-3" />
                      <span>Trial: {subscription.daysRemaining} days left</span>
                    </Badge>
                  )}
                  {subscription.status === 'paid' && (
                    <Badge variant="default" className="bg-green-600 flex items-center gap-1 py-1">
                      <Crown className="h-3 w-3" />
                      <span>Premium</span>
                    </Badge>
                  )}
                  {subscription.status === 'free' && (
                    <Badge variant="outline" className="flex items-center gap-1 py-1">
                      <Tag className="h-3 w-3" />
                      <span>Free</span>
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {subscription.status === 'trial' 
                  ? `Your trial ends on ${subscription.trialExpiresOn?.toLocaleDateString()}`
                  : subscription.status === 'free'
                  ? "Click to upgrade to Premium or manage your subscription"
                  : "Click to manage your subscription"}
              </TooltipContent>
            </Tooltip>
          )}
          {isMobile && (
            <div className="flex items-center gap-1 md:gap-2">
              {navigationItems.map((item) => (
                <Button
                  key={item.href}
                  variant="ghost"
                  size="icon"
                  asChild
                  className={cn(
                    location.pathname === item.href && "bg-secondary"
                  )}
                >
                  <Link to={item.href}>
                    <item.icon className="h-5 w-5" />
                  </Link>
                </Button>
              ))}
            </div>
          )}
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}
